import { useState } from 'react'
import {
  type MaterialComponent,
  type MaterialComposition,
} from '@esf/shared'

interface MaterialCompositionFormProps {
  composition: MaterialComposition | null
  onChange: (composition: MaterialComposition) => void
  disabled?: boolean
}

const emptyComponent: MaterialComponent = {
  name: '',
  materialType: '',
  weightGrams: 0,
  percentageByWeight: 0,
  isRecycled: false,
  preConsumerRecycledPercent: 0,
  postConsumerRecycledPercent: 0,
  originCountry: '',
}

const defaultComposition: MaterialComposition = {
  totalWeightGrams: 0,
  components: [],
  totalRecycledContentPercent: 0,
  preConsumerRecycledPercent: 0,
  postConsumerRecycledPercent: 0,
}

export default function MaterialCompositionForm({
  composition,
  onChange,
  disabled,
}: MaterialCompositionFormProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const data = composition || defaultComposition

  const updateField = <K extends keyof MaterialComposition>(
    field: K,
    value: MaterialComposition[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const addComponent = () => {
    const newComponents = [...data.components, { ...emptyComponent }]
    onChange({ ...data, components: newComponents })
    setExpandedIndex(newComponents.length - 1)
  }

  const removeComponent = (index: number) => {
    const newComponents = data.components.filter((_, i) => i !== index)
    onChange({ ...data, components: newComponents })
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
  }

  const updateComponent = (index: number, field: keyof MaterialComponent, value: string | number | boolean) => {
    const newComponents = data.components.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    )
    onChange({ ...data, components: newComponents })
  }

  // Calculate totals
  const calculateTotals = () => {
    const totalWeight = data.components.reduce((sum, c) => sum + (c.weightGrams || 0), 0)
    let totalRecycled = 0
    let totalPreConsumer = 0
    let totalPostConsumer = 0

    data.components.forEach(c => {
      if (c.isRecycled && totalWeight > 0) {
        const componentWeight = c.weightGrams || 0
        const componentPercent = componentWeight / totalWeight
        totalRecycled += componentPercent * 100
        totalPreConsumer += componentPercent * (c.preConsumerRecycledPercent || 0)
        totalPostConsumer += componentPercent * (c.postConsumerRecycledPercent || 0)
      }
    })

    onChange({
      ...data,
      totalWeightGrams: totalWeight,
      totalRecycledContentPercent: Math.round(totalRecycled * 10) / 10,
      preConsumerRecycledPercent: Math.round(totalPreConsumer * 10) / 10,
      postConsumerRecycledPercent: Math.round(totalPostConsumer * 10) / 10,
    })
  }

  const materialTypes = [
    'Steel',
    'Aluminium',
    'Stainless Steel',
    'Recycled Plastic',
    'Virgin Plastic',
    'HDPE',
    'LDPE',
    'PET',
    'Wood',
    'FSC Certified Wood',
    'Concrete',
    'Glass',
    'Rubber',
    'Textile',
    'Composite',
    'Other',
  ]

  return (
    <div className="space-y-6">
      {/* Summary Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-green-800">Material Composition Summary</h4>
          <button
            type="button"
            onClick={calculateTotals}
            disabled={disabled}
            className="text-xs text-green-600 hover:text-green-800 font-medium"
          >
            Recalculate Totals
          </button>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-green-600 mb-1">Total Weight</p>
            <p className="text-lg font-bold text-green-900">{(data.totalWeightGrams / 1000).toFixed(2)} kg</p>
          </div>
          <div>
            <p className="text-xs text-green-600 mb-1">Total Recycled</p>
            <p className="text-lg font-bold text-green-900">{data.totalRecycledContentPercent || 0}%</p>
          </div>
          <div>
            <p className="text-xs text-green-600 mb-1">Pre-Consumer</p>
            <p className="text-lg font-bold text-green-900">{data.preConsumerRecycledPercent || 0}%</p>
          </div>
          <div>
            <p className="text-xs text-green-600 mb-1">Post-Consumer</p>
            <p className="text-lg font-bold text-green-900">{data.postConsumerRecycledPercent || 0}%</p>
          </div>
        </div>
      </div>

      {/* Components List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-medium text-gray-700">Material Components</h4>
            <p className="text-xs text-gray-500">Track individual materials and recycled content</p>
          </div>
          <button
            type="button"
            onClick={addComponent}
            disabled={disabled}
            className="btn btn-secondary text-sm py-1 px-3"
          >
            + Add Material
          </button>
        </div>

        {data.components.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No materials added</p>
            <p className="text-xs text-gray-400">Add materials to track composition</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.components.map((component, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    {component.isRecycled && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Recycled
                      </span>
                    )}
                    <span className="font-medium text-sm">
                      {component.name || component.materialType || 'Unnamed material'}
                    </span>
                    {component.weightGrams > 0 && (
                      <span className="text-xs text-gray-500">{component.weightGrams}g</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {component.percentageByWeight > 0 && (
                      <span className="text-xs text-gray-500">{component.percentageByWeight}%</span>
                    )}
                    <svg
                      className={`h-5 w-5 text-gray-400 transition-transform ${
                        expandedIndex === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedIndex === index && (
                  <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Component Name</label>
                        <input
                          type="text"
                          value={component.name}
                          onChange={e => updateComponent(index, 'name', e.target.value)}
                          disabled={disabled}
                          className="filter-input w-full"
                          placeholder="e.g., Main frame"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Material Type</label>
                        <select
                          value={component.materialType}
                          onChange={e => updateComponent(index, 'materialType', e.target.value)}
                          disabled={disabled}
                          className="filter-select w-full"
                        >
                          <option value="">Select material</option>
                          {materialTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight (grams)</label>
                        <input
                          type="number"
                          value={component.weightGrams || ''}
                          onChange={e => updateComponent(index, 'weightGrams', e.target.value ? parseFloat(e.target.value) : 0)}
                          disabled={disabled}
                          className="filter-input w-full"
                          placeholder="0"
                          min="0"
                          step="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">% by Weight</label>
                        <input
                          type="number"
                          value={component.percentageByWeight || ''}
                          onChange={e => updateComponent(index, 'percentageByWeight', e.target.value ? parseFloat(e.target.value) : 0)}
                          disabled={disabled}
                          className="filter-input w-full"
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Origin Country</label>
                        <input
                          type="text"
                          value={component.originCountry || ''}
                          onChange={e => updateComponent(index, 'originCountry', e.target.value)}
                          disabled={disabled}
                          className="filter-input w-full"
                          placeholder="e.g., UK"
                        />
                      </div>
                    </div>

                    {/* Recycled Content */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="checkbox"
                          id={`recycled-${index}`}
                          checked={component.isRecycled}
                          onChange={e => updateComponent(index, 'isRecycled', e.target.checked)}
                          disabled={disabled}
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <label htmlFor={`recycled-${index}`} className="text-sm font-medium text-green-800">
                          Contains recycled content
                        </label>
                      </div>

                      {component.isRecycled && (
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">
                              Pre-Consumer Recycled %
                            </label>
                            <input
                              type="number"
                              value={component.preConsumerRecycledPercent || ''}
                              onChange={e => updateComponent(index, 'preConsumerRecycledPercent', e.target.value ? parseFloat(e.target.value) : 0)}
                              disabled={disabled}
                              className="filter-input w-full"
                              placeholder="0"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                            <p className="text-xs text-green-600 mt-1">Manufacturing waste</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">
                              Post-Consumer Recycled %
                            </label>
                            <input
                              type="number"
                              value={component.postConsumerRecycledPercent || ''}
                              onChange={e => updateComponent(index, 'postConsumerRecycledPercent', e.target.value ? parseFloat(e.target.value) : 0)}
                              disabled={disabled}
                              className="filter-input w-full"
                              placeholder="0"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                            <p className="text-xs text-green-600 mt-1">Consumer waste streams</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => removeComponent(index)}
                        disabled={disabled}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove Material
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verification Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex gap-2">
          <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800">Verification Required</p>
            <p className="text-xs text-amber-700 mt-1">
              Recycled content claims must be verified per ESPR requirements. Ensure you have documentation from suppliers.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
